// ===========================================
// Birthdays This Week (admin dashboard modal, 2026-09-07)
// ===========================================
// Opened from the Birthdays stat card — the first onClick card on that page.
// The list is fetched separately from the stats because names and ages are
// student data and sit behind `people_students.read`, while the bare count on
// the card rides the dashboard permission.

import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CakeIcon from '@mui/icons-material/Cake';
import { Link } from 'react-router-dom';
import { useWeeklyBirthdays } from '../../hooks/useDashboard';
import { formatUTCDate } from '../../utils/dateUtils';

export interface BirthdaysThisWeekModalProps {
  open: boolean;
  onClose: () => void;
  /** How many students still carry the unknown-birth-date sentinel. */
  missingBirthDateCount?: number;
  /** Where the "needs a real birth date" link goes (school-scoped basePath). */
  studentsPath: string;
}

/** `Wed 10/09/2026` — dd/mm/yyyy per CLAUDE.md rule 7, from a @db.Date with UTC getters. */
function formatBirthday(dateKey: string): string {
  return formatUTCDate(`${dateKey}T00:00:00.000Z`, {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export default function BirthdaysThisWeekModal({
  open,
  onClose,
  missingBirthDateCount = 0,
  studentsPath,
}: BirthdaysThisWeekModalProps) {
  const { data, isLoading, isError } = useWeeklyBirthdays({ enabled: open });
  const students = data?.students ?? [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CakeIcon color="secondary" /> Birthdays this week
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && <Skeleton variant="rectangular" height={160} />}
        {isError && <Alert severity="error">Could not load this week&apos;s birthdays.</Alert>}
        {!isLoading && !isError && students.length === 0 && (
          <Typography color="text.secondary">No student birthdays this week.</Typography>
        )}
        {!isLoading && !isError && students.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Student</TableCell>
                <TableCell>Birthday</TableCell>
                <TableCell>Turning</TableCell>
                <TableCell>Class</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {students.map((student) => (
                <TableRow key={student.studentId} data-testid={`birthday-row-${student.studentId}`}>
                  <TableCell>{student.firstName} {student.lastName}</TableCell>
                  <TableCell>{formatBirthday(student.dateKey)}</TableCell>
                  <TableCell>{student.ageTurning}</TableCell>
                  <TableCell>
                    {student.classes.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">No current class</Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {student.classes.map((cls) => (
                          <Box key={cls.lessonId} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2">
                              {cls.lessonName}{cls.startTime ? ` · ${cls.startTime}` : ''}
                            </Typography>
                            {cls.onTheirLessonDay && (
                              <Chip
                                label="On their lesson day"
                                size="small"
                                color="warning"
                                data-testid={`on-lesson-day-${cls.lessonId}`}
                              />
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {missingBirthDateCount > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              {missingBirthDateCount} student{missingBirthDateCount === 1 ? '' : 's'} need a real birth date on record —
              they are recorded as 1 January and are left out of this list.{' '}
              <Link to={`${studentsPath}?filter=missing-birth-date`} onClick={onClose}>
                Review them
              </Link>
            </Typography>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
